/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

/**
 * Customizes the MUI theme by overwriting the default MUI styles
 * with the given components styles in the 'overrides' key of the theme.
 * (https://v3.material-ui.com/customization/themes/#customizing-all-instances-of-a-component-type)
 * It is used within the hook {@link useTheming.js}.
 */

export const overrides = theme => ({
  MuiTypography: {
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '1rem',
    },
    h1: {
      fontSize: '1.75rem',
      lineHeight: '2.625rem',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('xl')]: {
        fontSize: '2.75rem',
        lineHeight: '4.5rem',
      },
      [theme.breakpoints.up('xl3')]: {
        fontSize: '3.625rem',
      },
    },
    h2: {
      fontSize: '1.375rem',
      fontWeight: '600',
      lineHeight: '2.063rem',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('xl')]: {
        fontSize: '1.938rem',
        lineHeight: '2.813rem',
      },
      [theme.breakpoints.up('xl3')]: {
        fontSize: '2.25rem',
        lineHeight: '3.375rem',
      },
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: '600',
      lineHeight: '1.875rem',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('xl')]: {
        fontSize: '1.563rem',
        lineHeight: '2.438rem',
      },
      [theme.breakpoints.up('xl3')]: {
        fontSize: '1.75rem',
        lineHeight: '2.125rem',
      },
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: '600',
      lineHeight: '1.688rem',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('xl')]: {
        fontSize: '1.25rem',
        lineHeight: '1.875rem',
      },
      [theme.breakpoints.up('xl3')]: {
        fontSize: '1.375rem',
        lineHeight: '1.75rem',
      },
    },
    h5: {
      fontSize: '1rem',
      lineHeight: '1.5rem',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('xl3')]: {
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
      },
    },
    h6: {
      display: 'flex',
      alignItems: 'center',
      lineHeight: '1.25rem',
      [theme.breakpoints.up('xl3')]: {
        lineHeight: '1.5rem',
      },
    },
  },
  MuiPaper: {
    root: {
      backgroundColor: theme.palette.white,
    },
  },
  MuiTable: {
    root: {
      // Workaround to show round real borders on tables
      borderCollapse: 'separate',
      borderSpacing: 0,
      borderTop: `calc(1rem / 16) solid ${theme.palette.border}`,
      borderLeft: `calc(1rem / 16) solid ${theme.palette.border}`,
      borderRight: `calc(1rem / 16) solid ${theme.palette.border}`,
      borderTopLeftRadius: '0.5rem',
      borderTopRightRadius: '0.5rem',
    },
  },
  MuiTableRow: {
    root: {
      backgroundColor: 'white',
      '&.MuiTableRow-hover:hover': {
        cursor: 'pointer',
        backgroundColor: theme.palette.tableHover,
      },
    },
  },
  MuiTableCell: {
    root: {
      borderRight: `calc(1rem / 16) solid ${theme.palette.highlightsAndDividers}`,
      borderBottom: `calc(1rem / 16) solid ${theme.palette.highlightsAndDividers}`,
      height: '3.25rem', // setting fixed table height untill we get to table refactor
      [theme.breakpoints.up('xl3')]: {
        height: '3.875rem',
      },
      '&:last-child': {
        borderRight: 'none',
      },
      fontSize: '1rem',
      [theme.breakpoints.up('xl3')]: {
        fontSize: '1.125rem',
      },
    },
    head: {
      backgroundColor: theme.palette.elementBackground,
      '&:first-child': {
        borderTopLeftRadius: '0.5rem',
      },
      '&:last-child': {
        borderTopRightRadius: '0.5rem',
      },
    },
    footer: {
      backgroundColor: theme.palette.elementBackground,
      borderBottomLeftRadius: '.5rem',
      borderBottomRightRadius: '.5rem',
    },
    sizeSmall: {
      '&$head': {
        paddingTop: '.625rem',
        paddingBottom: '.625rem',
      },
      '&$body': {
        paddingTop: 'calc(2rem / 16)',
        paddingBottom: 'calc(2rem / 16)',
        whiteSpace: 'noWrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  },
  MuiCard: {
    root: {
      '&[data-focustype="key"]': {
        outline: 'none',
        boxShadow: `0 0 0 1px #fff, 0 0 0 4px ${theme.palette.focus} !important`,
      },

      '& active, &:focus': {
        outline: 'none',
      },
    },
  },
  MuiCardHeader: {
    root: {
      lineHeight: 0,
      color: theme.palette.summaryCardHeaderText,
      [theme.breakpoints.down('sm')]: {
        padding: '0.125rem',
        lineHeight: 1.5,
      },
    },
    avatar: {
      [theme.breakpoints.down('sm')]: {
        marginTop: '4px',
      },
      '& svg': {
        fontSize: '2rem',
        [theme.breakpoints.down('sm')]: {
          fontSize: '1.5rem',
        },
      },
    },
    action: {
      alignSelf: 'center',
      marginTop: 0,
      marginRight: 0,
    },
  },
  MuiCardContent: {
    color: theme.palette.summaryCardHeaderText,
    [theme.breakpoints.down('sm')]: {
      padding: '0.125rem',
    },
  },
  MuiLink: {
    button: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem',
      height: '2.75rem',
      padding: '6px 16px',
      minWidth: 'max-content',
      boxSizing: 'border-box',
      transition: 'background-color 250ms',
      textDecoration: 'underline',
      borderRadius: '4px',
      '&.Mui-focusVisible': {
        outline: 'none',
      },
      '& > .MuiSvgIcon-root': {
        marginLeft: '0.5rem',
        paddingBottom: '3px',
      },
    },
    underlineAlways: {
      '&:visited': {
        color: `${theme.palette.visitedLink}`,
      },
    },
  },
  MuiButton: {
    root: {
      fontWeight: '600',
      whiteSpace: 'nowrap',
      minWidth: 'max-content',
      '&[data-focustype="key"]': {
        boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
      },
    },
    disableElevation: {
      '&[data-focustype="key"]': {
        boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
      },
    },
    containedPrimary: {
      // primary
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.hoverContrastText,
        backgroundColor: theme.palette.primary.hover,
      },
      '&:focus, &:active': {
        color: theme.palette.primary.activeText,
        backgroundColor: theme.palette.primary.active,
      },
      '&:disabled': {
        color: theme.palette.primary.disabledText,
        backgroundColor: theme.palette.primary.disabled,
      },
      '&[data-focustype="key"]': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    contained: {
      // default (in code) == secondary (in figma)
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        color: theme.palette.secondary.hoverContrastText,
        backgroundColor: theme.palette.secondary.hover,
      },
      '&:focus, &:active': {
        backgroundColor: theme.palette.secondary.main,
      },
      '&:disabled, &.Mui-disabled': {
        color: theme.palette.secondary.disabledText,
        backgroundColor: theme.palette.secondary.disabled,
      },
    },
    containedSecondary: {
      // inverted
      color: theme.palette.inverted.contrastText,
      backgroundColor: theme.palette.inverted.main,
      '&:hover': {
        color: theme.palette.inverted.hoverContrastText,
        backgroundColor: theme.palette.inverted.hover,
      },
      '&:focus, &:active': {
        color: theme.palette.inverted.contrastText,
        backgroundColor: theme.palette.inverted.active,
      },
      '&:disabled': {
        color: theme.palette.inverted.disabledText,
        backgroundColor: theme.palette.inverted.disabled,
      },
      '&[data-focustype="key"]': {
        backgroundColor: theme.palette.inverted.main,
        color: theme.palette.inverted.contrastText,
      },
    },
    startIcon: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  },
  MuiIconButton: {
    root: {
      width: '2rem',
      height: '2rem',
      [theme.breakpoints.up('xl3')]: {
        width: '2.5rem',
        height: '2.5rem',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '-2px',
        top: '-2px',
        width: '44px',
        height: '44px',
      },
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        color: theme.palette.secondary.hoverContrastText,
        backgroundColor: theme.palette.secondary.hover,
      },
      '&:focus, &:active': {
        color: theme.palette.secondary.contrastText,
        backgroundColor: theme.palette.secondary.active,
      },
      '&:disabled': {
        color: theme.palette.secondary.disabledText,
        backgroundColor: theme.palette.secondary.disabled,
      },
      '&[data-focustype="key"]': {
        color: theme.palette.secondary.contrastText,
        backgroundColor: theme.palette.secondary.main,
      },
      '& svg, & span': {
        fontSize: '1.5rem',
        [theme.breakpoints.up('xl2')]: {
          fontSize: '1.625rem',
        },
      },
    },
    colorPrimary: {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
      },
      '&:focus, &:active': {
        color: theme.palette.inverted.contrastText,
        backgroundColor: theme.palette.inverted.active,
      },
      '&:disabled': {
        color: theme.palette.primary.disabled,
        backgroundColor: 'transparent',
      },
      '&[data-focustype="key"]': {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
      },
    },
    sizeSmall: {
      width: '2.2rem',
      height: '2.2rem',
      '& svg': {
        width: '.9em',
        height: '.9em',
      },
    },
  },
  MuiRadio: {
    root: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-focusVisible:not(.Mui-checked) > .MuiIconButton-label > span': {
        '&:after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          width: '1.7rem',
          height: '1.7rem',
          border: `3px solid ${theme.palette.primary.main}`,
          borderRadius: '1rem',
          backgroundColor: 'transparent',
        },
      },
      '&.Mui-focusVisible.Mui-checked > .MuiIconButton-label ': {
        border: `3px solid ${theme.palette.primary.main}`,
        padding: '2px 1px',
        width: '1.7rem',
        height: '1.7rem',
        borderRadius: '1rem',
      },
      '&.Mui-disabled.Mui-checked > .MuiIconButton-label > span': {
        backgroundColor: theme.palette.darkGray,
      },
    },
    colorPrimary: {
      '&$checked': {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiCheckbox: {
    root: {
      padding: '8px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& > .MuiIconButton-label': {
        width: '1.5rem',
      },
      '&.Mui-focusVisible:not(.Mui-checked) > .MuiIconButton-label > span': {
        '&:after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          width: '1.7rem',
          height: '1.7rem',
          padding: '1px',
          border: `3px solid ${theme.palette.primary.main}`,
          borderRadius: '0.5rem',
          backgroundColor: 'transparent',
        },
      },
      '&.Mui-focusVisible.Mui-checked > .MuiIconButton-label ': {
        border: `3px solid ${theme.palette.primary.main}`,
        padding: '2px 1px',
        width: '1.7rem',
        height: '1.7rem',
        borderRadius: '0.5rem',
      },
    },
    colorPrimary: {
      '&$checked': {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiToggleButton: {
    root: {
      whiteSpace: 'nowrap',
      minWidth: 'max-content',
      backgroundColor: theme.palette[theme.toggleButton].main,
      color: theme.palette[theme.toggleButton].contrastText,
      '&:focus, &:active': {
        backgroundColor: theme.palette[theme.toggleButton].active,
      },
      '&:hover': {
        backgroundColor: theme.palette[theme.toggleButton].hover,
      },
      '&$selected': {
        backgroundColor: theme.palette[theme.toggleButtonActive].main,
        color: theme.palette[theme.toggleButtonActive].contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette[theme.toggleButtonActive].active,
        },
        '&:hover': {
          backgroundColor: theme.palette[theme.toggleButtonActive].hover,
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    root: {
      border: 'calc(1rem / 16) solid #CBD0D6',

      '& .MuiToggleButtonGroup-groupedHorizontal:not(:last-child)': {
        marginRight: '0.5rem',
      },
    },
  },
  MuiInputBase: {
    input: {
      background: theme.palette.white,
      '&.Mui-disabled': {
        color: theme.palette.disabled,
        backgroundColor: 'transparent',
        borderRadius: '4px',
        zIndex: 10,
        opacity: 1,
      },
    },
    multiline: {
      '& > div > .MuiInputAdornment-root': {
        marginLeft: '18px',
      },
    },
    root: {
      '&$disabled': {
        '& > textarea': {
          marginRight: '1px',
        },
        '& > fieldset': {
          background: theme.palette.backgroundDisabled,
        },
      },
    },
  },
  MuiSelect: {
    outlined: {
      '&:focus': {
        backgroundColor: `${theme.palette.white}`,
      },
    },
    icon: {
      '&$disabled': {
        zIndex: 1,
      },
    },
  },
  MuiInputLabel: {
    root: {
      color: theme.palette.input,
    },
    outlined: {
      '&$marginDense': {
        transform: 'translate(.875rem, 1rem)',
        '&$shrink': {
          transform: 'translate(.875rem, -.375rem)',
        },
      },
      '&$shrink': {
        transform: 'translate(.875rem, -.375rem)',
      },
      '&$disabled': {
        zIndex: 11,
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      '&:disabled, &.Mui-disabled': {
        '& fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.input,
        },
      },
      '& fieldset': {
        border: `calc(1rem / 16) solid ${theme.palette.input}`,
        '& > legend': {
          fontSize: 'initial',
        },
      },
    },
    adornedEnd: {
      paddingRight: '0px',
    },
    notchedOutline: {
      '&:disabled, &.Mui-disabled': {
        '& fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.input,
        },
      },
      '& fieldset': {
        border: `calc(1rem / 16) solid ${theme.palette.input}`,
      },
    },
    marginDense: {
      '& fieldset': {
        border: `calc(1rem / 16) solid ${theme.palette.input}`,
        '& > legend': {
          fontSize: 'initial',
        },
        '&:disabled': {
          borderColor: theme.palette.disabled,
        },
      },
    },
    inputMarginDense: {
      borderRadius: '4px',
      paddingTop: '.875rem',
      paddingBottom: '.875rem',
    },
  },
  MuiFormLabel: {
    root: {
      '&.Mui-disabled': {
        color: theme.palette.disabled,
      },
    },
    asterisk: {
      display: theme.fields.required.displayAsterisk,
    },
  },
  MuiFormControlLabel: {
    root: {
      '&$disabled > span.MuiTypography-root': {
        color: theme.palette.disabledCheckbox,
      },
    },
    label: {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '-0.5rem',
        right: '-0.9px',
        bottom: '-0.5px',
        left: '-0.13px',
      },
    },
  },
  MuiFormHelperText: {
    root: {
      fontSize: 'initial',
    },
  },
  MuiTextField: {
    root: {
      '& label.Mui-focused': {
        color: '#21252C',
        background: theme.palette.white,
        paddingLeft: '6px',
        paddingRight: '5px',
      },
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: `${theme.palette.mainText}`,
          border: `1px solid ${theme.palette.mainText}`,
          boxShadow: `0 0 0 1px #fff, 0 0 0 4px ${theme.palette.focus}`,
        },
      },
    },
  },
  MuiButtonBase: {
    root: {
      '& .MuiButtonContained': {
        '&.Mui-focusVisible': {
          color: `${theme.palette.focus}`,
          boxShadow: `0 0 0 1px #fff, 0 0 0 4px ${theme.palette.focus}`,
        },
      },
    },
  },
  MuiCssBaseline: {
    '@global': {
      /* TO-DO: Move it to MuiOutlinedInput section */
      '.MuiOutlinedInput-multiline': {
        background: theme.palette.white,
      },
      html: {
        scrollBehavior: 'smooth',
      },

      'button,p,h1,h2,h3,h4,h5,h6': {
        '&[data-focustype="key"]': {
          boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
        },
        '&:focus, &:active': {
          outline: 'none',
        },
      },
      '.basicFilingInfo': {
        '& > div :nth-child(5)': {
          display: 'flex',
          flexWrap: 'wrap',
        },
        '& .MuiGrid-item:nth-child(5), .MuiGrid-item:nth-child(2), .MuiGrid-item:nth-child(3)': {
          flex: 1,
        },
      },
      '.basicFilingInfoWithTitleGroup': {
        '& .MuiGrid-item:nth-child(4), .MuiGrid-item:nth-child(5)': {
          flex: 1,
        },
      },
    },
  },
  MuiDropzonePreviewList: {
    position: 'relative',
    removeButton: {
      opacity: '1',
      top: '.5rem',
      right: '4rem',
    },
  },
  MUIDataTable: {
    paper: {
      padding: '1.5rem',
    },
  },
  MuiAppBar: {
    root: {
      backgroundColor: theme.palette.contrastBackground,
    },
    positionSticky: {
      padding: '0.75rem 1.5rem',
      [theme.breakpoints.down('sm')]: {
        padding: '0.5rem 1rem',
      },
      [theme.breakpoints.up('xl2')]: {
        padding: '1.5rem 5.75rem',
      },
    },
  },
  MuiChip: {
    root: {
      fontSize: '1rem',
      '&[class*="default"]': {
        backgroundColor: theme.palette.statusChip.default.main,
        color: theme.palette.statusChip.default.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.default.main,
        },
      },
      '&[class*="DRAFT"],[class*="VALID_DRAFT"],[class*="PENDING"]': {
        backgroundColor: theme.palette.statusChip.DRAFT.main,
        color: theme.palette.statusChip.DRAFT.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.DRAFT.main,
        },
      },
      '&[class*="READY_TO_SIGN"],[class*="VALID_READY_TO_SIGN"]': {
        backgroundColor: theme.palette.statusChip.READY_TO_SIGN.main,
        color: theme.palette.statusChip.READY_TO_SIGN.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.READY_TO_SIGN.main,
        },
      },
      '&[class*="SENT_TO_SIGN"]': {
        backgroundColor: theme.palette.statusChip.SENT_TO_SIGN.main,
        color: theme.palette.statusChip.SENT_TO_SIGN.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT_TO_SIGN.main,
        },
      },
      '&[class*="SIGNED"]': {
        backgroundColor: theme.palette.statusChip.READY_TO_SEND.main,
        color: theme.palette.statusChip.READY_TO_SEND.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.READY_TO_SEND.main,
        },
      },
      '&[class*="SENT_TO_FILING"]': {
        backgroundColor: theme.palette.statusChip.SENT_TO_FILING.main,
        color: theme.palette.statusChip.SENT_TO_FILING.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT_TO_FILING.main,
        },
      },
      '&[class*="SENT"]': {
        backgroundColor: theme.palette.statusChip.SENT.main,
        color: theme.palette.statusChip.SENT.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT.main,
        },
      },
      '&[class*="SENDING_FAILED"],[class*="DECLINED"]': {
        backgroundColor: theme.palette.statusChip.SENDING_FAILED.main,
        color: theme.palette.statusChip.SENDING_FAILED.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENDING_FAILED.main,
        },
      },
      '&[class*="READY_TO_PAY"]': {
        backgroundColor: theme.palette.statusChip.READY_TO_PAY.main,
        color: theme.palette.statusChip.READY_TO_PAY.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.READY_TO_PAY.main,
        },
      },
      '&[class*="RESIGNING_FAILED"]': {
        backgroundColor: theme.palette.statusChip.RESIGNING_FAILED.main,
        color: theme.palette.statusChip.RESIGNING_FAILED.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.RESIGNING_FAILED.main,
        },
      },
      '&[class*="SIGNING_FAILED"]': {
        backgroundColor: theme.palette.statusChip.SIGNING_FAILED.main,
        color: theme.palette.statusChip.SIGNING_FAILED.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SIGNING_FAILED.main,
        },
      },
      '&[class*="EXPIRED"]': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.statusChip.SIGNING_FAILED.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.error.main,
        },
      },
      '&[class*="ACCEPTED"]': {
        backgroundColor: theme.palette.statusChip.SENT.main,
        color: theme.palette.statusChip.SENT.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT.main,
        },
      },
      '&[class*="DECLINED"]': {
        backgroundColor: theme.palette.statusChip.SENDING_FAILED.main,
        color: theme.palette.statusChip.SENDING_FAILED.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT.main,
        },
      },
      '&[class*="PENDING"]': {
        backgroundColor: theme.palette.statusChip.DRAFT.main,
        color: theme.palette.statusChip.DRAFT.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.statusChip.SENT.main,
        },
      },
      '&[class*="ROLE_DRAFTER"]': {
        backgroundColor: theme.palette.roleChip.DRAFTER.main,
        color: theme.palette.roleChip.DRAFTER.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.roleChip.DRAFTER.main,
        },
      },
      '&[class*="ROLE_SIGNER"]': {
        backgroundColor: theme.palette.roleChip.SIGNER.main,
        color: theme.palette.roleChip.SIGNER.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.roleChip.SIGNER.main,
        },
      },
      '&[class*="ROLE_SENDER"]': {
        backgroundColor: theme.palette.roleChip.SENDER.main,
        color: theme.palette.roleChip.SENDER.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.roleChip.SENDER.main,
        },
      },
      '&[class*="ROLE_PAYER"]': {
        backgroundColor: theme.palette.roleChip.PAYER.main,
        color: theme.palette.roleChip.PAYER.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.roleChip.PAYER.main,
        },
      },
      '&[class*="ROLE_END_USER_ADMIN"]': {
        backgroundColor: theme.palette.roleChip.ADMIN.main,
        color: theme.palette.roleChip.ADMIN.contrastText,
        '&:focus, &:active': {
          backgroundColor: theme.palette.roleChip.ADMIN.main,
        },
      },
    },
    label: {
      '& > .MuiBox-root': {
        alignItems: 'center',
      },
    },
    sizeSmall: {
      [theme.breakpoints.up('xl3')]: {
        height: '1.75rem',
      },
      '& span[role=img]': {
        display: 'inline-block',
        position: 'relative',
        width: '1.5rem',
        height: '1.5rem',
        marginRight: '0',
        left: '0.438rem',

        borderRadius: '50%',
        [theme.breakpoints.up('xl3')]: {
          width: '1.625rem',
          height: '1.625rem',
        },
      },
      '&[data-focustype="key"]': {
        boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
      },
    },
    deleteIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: theme.palette.black,
      marginLeft: '0',
    },
    deleteIconSmall: {
      marginLeft: '0',
    },
  },
  MuiFab: {
    root: {
      boxShadow: 'none',
    },
  },
  MuiTooltip: {
    tooltip: {
      backgroundColor: theme.palette.tooltipBackground,
      maxWidth: theme.tooltipMaxWidth,
    },
    arrow: {
      color: theme.palette.tooltipBackground,
    },
  },
  MuiDialog: {
    paper: {
      borderRadius: '1rem',
      padding: '3.5rem 4rem',
      overflowX: 'hidden',
      [theme.breakpoints.down('sm')]: {
        borderRadius: '0.5rem',
        padding: '1rem',
      },
      [theme.breakpoints.up('xl2')]: {
        width: '62.5rem',
      },
    },
    paperWidthMd: {
      maxWidth: '640px', // normal bp
    },
    paperWidthLg: {
      maxWidth: '1000px', // normal bp
    },
    container: {
      '& > .MuiDialog-paper > .MuiButtonBase-root': {
        // dialog close button
        backgroundColor: 'transparent',
        position: 'absolute',
        right: '1.25rem',
        top: '1rem',
        [theme.breakpoints.down('sm')]: {
          top: '1%',
          right: '1%',
        },
      },
    },
  },
  MuiDialogTitle: {
    root: {
      padding: '0 0 1rem 0',
      '& .MuiTypography-root': {
        display: 'flex',
        alignItems: 'start',
        '& > .MuiSvgIcon-root': {
          // dialog satatus icon
          margin: '0.4rem 0.75rem 0 0',
        },
      },
    },
  },
  MuiDialogContent: {
    root: {
      padding: 0,
      fontSize: '1rem',
      '& .rjsf': {
        // dialog containing rjsf forms
        width: '95%',
        '.MuiGrid-root': {
          paddingLeft: 0,
        },
        '& > .form-group': {
          '& > .MuiGrid-container': {
            width: '95%',
            margin: '0',
            '& > .MuiGrid-root.MuiGrid-item:first-child': {
              // remove spacing between separate FormSectionField elements in dialog containing rjsf
              paddingBottom: 0,
            },
          },
          '& .rjsf .MuiFormControl-root, .rjsf .MuiFormControl-fullWidth': {
            maxWidth: '95%',
          },
        },
      },
    },
  },
  MuiDialogActions: {
    root: {
      padding: '2.5rem 0 0 0',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: '2rem 0 0 0',
      },
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        '& > button:not(:first-child)': {
          marginLeft: '1.5rem',
        },
      },
    },
    spacing: {
      '& > :not(:first-child)': {
        [theme.breakpoints.down('md')]: {
          marginLeft: '0',
          marginTop: '1rem',
        },
      },
    },
  },
  MuiTab: {
    root: {
      [theme.breakpoints.up('xs')]: {
        minWidth: '300px',
        maxWidth: '500px',
        padding: '0.375rem 1.25rem',
        whiteSpace: 'nowrap',
        fontSize: '1.25rem',
        color: theme.palette.tab,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '1.563rem',
      },
      [theme.breakpoints.up('xl')]: {
        padding: '0.375rem 1.875rem',
      },
      [theme.breakpoints.up('xl3')]: {
        fontSize: '1.75rem',
      },
      '&[data-focustype="key"]': {
        outline: 'none',
        boxShadow: `inset 0 0 0 3px ${theme.palette.focus}`,
        borderRadius: '0.25rem',
      },
    },
    textColorPrimary: {
      color: theme.palette.titleText,
      fontWeight: theme.tabSectionUnderlineWeight,
      letterSpacing: 0,
      marginBottom: '0.375rem',
      '&:hover': {
        color: theme.palette.tabActive,
      },
      '&$selected': {
        color: theme.palette.tabActive,
      },
    },
    wrapper: {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      lineHeight: 'normal',
      fontSize: '1.2rem',
    },
  },
  MuiTabs: {
    flexContainer: {
      padding: '2px 2px 0',
    },
    indicator: {
      borderRadius: '10px 10px 0px 0px',
      height: 3,
      backgroundColor: theme.palette.tabActive,
    },
  },
  MuiTabPanel: {
    root: {
      padding: '32px 0px 64px 0px',
      [theme.breakpoints.up('lg')]: {
        padding: '42px 0px 108px 0px',
      },
    },
  },
  MuiMenuItem: {
    root: {
      '&:focus-visible': {
        background: theme.palette.keyboardFocusBackground,
        boxShadow: `inset 0 0 0 3px ${theme.palette.focus}`,
        borderRadius: '4px',
      },
    },
  },
  WAMuiChipInput: {
    root: {
      backgroundColor: theme.palette.white,
      '&:focus-visible': {
        background: theme.palette.keyboardFocusBackground,
        border: `3px solid ${theme.palette.focus}`,
        borderRadius: '4px',
      },
      '& label.Mui-focused': {
        color: theme.palette.secondary.contrastText,
      },
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: `${theme.palette.focus}`,
        },
      },
    },
  },
  MuiAutocomplete: {
    root: {
      background: `${theme.palette.white}`,
    },
    endAdornment: {
      top: '0.563rem',
    },
  },
  MuiAlert: {
    action: {
      '& > .MuiIconButton-root': {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiGrid: {
    root: {
      '& > .MuiGrid-item:empty': {
        padding: '0',
      },
    },
  },
});
